/*
  PDP sticky buy box (mobile).

  The buy box is wrapped in Liquid (.product-buy-box around the variant picker
  and the buy buttons — .pbx-variant + .pbx-buttons). This file never moves the
  variant-selects / product-form custom elements: it only toggles a class, applies
  inline positioning, and manages a sentinel/spacer, so variant, availability and
  ajax-cart logic all keep working.

  Two modules live here:
    1. Swatch collapse — inside the stuck bar the colors collapse to a single row
       behind a "More Colors" toggle, so a product with many colors can't grow the
       bar to fill the viewport.
    2. Sticky bar — the box becomes position:fixed at the bottom once the real
       Add to Cart scrolls out of view.

  Loaded with `defer` and held until window load, so it never competes with the
  product page's own render.

  ROLLED OUT — the A/B test concluded and 'version' (the sticky bar) won, so it
  is now ON for every visitor. See the rollout block below.
*/
(function () {
  'use strict';

  // ---- Rollout (was the A/B gate) -----------------------------------------
  // The test's localStorage bucket (la_ab_pdp_sticky_buybox) is deliberately
  // IGNORED now: visitors bucketed 'default' during the test must get the
  // winner too, so the variant lives in memory and always starts as 'version'.
  //
  // QA kill switch, in the browser console:
  //   StickyBuyBoxAB.setVariant('default')   -> sticky off, this page view only
  //   StickyBuyBoxAB.setVariant('version')   -> back on
  // The choice is NOT persisted — a reload always returns to 'version'.
  var AB_KEY = 'la_ab_pdp_sticky_buybox'; // kept for reference/analytics only
  var VARIANT = 'version';

  function getVariant() { return VARIANT; }

  window.StickyBuyBoxAB = {
    key: AB_KEY,
    getVariant: getVariant,
    setVariant: function (v) {
      if (v !== 'version' && v !== 'default') { return null; }
      VARIANT = v;
      if (v === 'version') { start(); }
      else if (typeof window.__stickyBuyBoxStop === 'function') { window.__stickyBuyBoxStop(); }
      return v;
    }
  };
  // -------------------------------------------------------------------------

  /* ===================== 1. Swatch collapse ("More Colors") ================= */
  function initSwatchCollapse() {
    // Whether each swatch row is expanded ("Fewer Colors"). Kept OUTSIDE the DOM,
    // keyed by row index, so the choice survives the picker re-rendering its
    // innerHTML on every variant change — changing a color/size no longer snaps
    // an opened list shut.
    var expandedStates = {};

    // Collapse color swatches to a single row with a "More Colors" toggle.
    function applyRow(row, key) {
      var swatches = row.querySelector('.pbx-swatches');
      var btn = row.querySelector('.pbx-see-more');
      if (!swatches || !btn) { return; }

      // "More Colors" only applies inside the sticky ATC bar. On the product page
      // itself the picker shows every swatch in full.
      var boxEl = row.closest('.product-buy-box');
      if (!boxEl || !boxEl.classList.contains('is-stuck')) {
        btn.hidden = true;
        swatches.style.maxHeight = '';
        swatches.style.overflow = '';
        return;
      }

      // Collapse to the first row of swatches. On products with dye groups
      // (e.g. "Pigment Wash Dye") everything below the first row — including the
      // grouped swatches — is hidden until "More Colors".
      var labels = swatches.querySelectorAll('label');
      if (!labels.length) { btn.hidden = true; return; }

      // Measure natural layout with any clip removed.
      swatches.style.maxHeight = '';
      swatches.style.overflow = '';

      var firstTop = labels[0].offsetTop;
      var rowClip = 0;
      for (var i = 1; i < labels.length; i++) {
        if (labels[i].offsetTop > firstTop + 1) {
          // Clip exactly where row 2 begins so row 1 shows in full.
          rowClip = labels[i].offsetTop - firstTop;
          break;
        }
      }

      if (!rowClip) { btn.hidden = true; return; }

      btn.hidden = false;
      if (expandedStates[key]) {
        btn.textContent = 'Fewer Colors';
      } else {
        btn.textContent = 'More Colors';
        swatches.style.maxHeight = rowClip + 'px';
        swatches.style.overflow = 'hidden';
      }
    }

    function initAll() {
      document.querySelectorAll('.pbx-swatch-row').forEach(function (row, index) {
        var btn = row.querySelector('.pbx-see-more');
        if (btn && !btn.dataset.bound) {
          btn.dataset.bound = '1';
          btn.addEventListener('click', function () {
            expandedStates[index] = !expandedStates[index];
            applyRow(row, index);
          });
        }
        applyRow(row, index);
      });
    }

    var t;
    function onResize() { clearTimeout(t); t = setTimeout(initAll, 150); }

    initAll();
    window.addEventListener('resize', onResize);
    // Re-measure when the buy box docks/undocks (width changes).
    window.__pbxSeeMoreRefresh = initAll;

    // Forget every "Fewer Colors" expansion. expandedStates survives the picker's
    // re-render on purpose, so the sticky bar has to clear it explicitly on close
    // — otherwise it re-opens already expanded.
    window.__pbxSeeMoreCollapse = function () {
      expandedStates = {};
      initAll();
    };

    // On variant change the picker replaces its own innerHTML (global.js:
    // variant-selects.innerHTML = ...), which rebuilds the swatches fully expanded
    // and drops our clip. The .pbx-variant wrapper survives that swap, so watch it
    // and re-apply the collapse before the next paint — no flash of the bar
    // springing open.
    var variantPart = document.querySelector('.product-buy-box .pbx-variant');
    if (variantPart && 'MutationObserver' in window) {
      var reinitPending = false;
      new MutationObserver(function () {
        if (reinitPending) { return; }
        reinitPending = true;
        requestAnimationFrame(function () {
          reinitPending = false;
          initAll();
        });
      }).observe(variantPart, { childList: true, subtree: true });
    }
  }

  /* ===================== 2. Sticky bar ===================================== */
  function initStickyBox() {
    // Prefer the whole buy box; fall back to the add-to-cart buttons so we still
    // get a sticky element even if the Liquid wrapper isn't present.
    var box = document.querySelector('.product-buy-box')
           || document.querySelector('.product-form__buttons');
    if (!box || box.dataset.buyBoxInit) { return; }
    box.dataset.buyBoxInit = '1';

    // Sentinel sits ABOVE the box (stable in normal flow); the spacer reserves
    // the box's space below it while it's fixed, so nothing jumps. Reading the
    // sentinel is stable whether stuck or not -> no flicker.
    var sentinel = document.createElement('div');
    sentinel.innerHTML = '&nbsp;';
    sentinel.className = 'product-buy-box-sentinel';
    var spacer = document.createElement('div');
    spacer.className = 'product-buy-box-spacer';
    spacer.style.display = 'none';
    box.parentNode.insertBefore(sentinel, box);
    box.parentNode.insertBefore(spacer, box.nextSibling);

    var mq = window.matchMedia('(max-width: 749px)');
    var stuck = false;
    var suspend = false;   // true while "Change" has jumped the user to the picker
    var running = false;   // the rAF loop only runs while the A/B variant is on

    // Positioning is applied as INLINE styles so it can't be defeated by a CSS
    // media-query mismatch, specificity, or a transformed ancestor. The mobile
    // gate lives in check(); .is-stuck stays on too (for any extra theming).
    var STUCK_STYLE = {
      position: 'fixed',
      left: '0',
      right: '0',
      bottom: '0',
      width: '100%',
      zIndex: '1000000000',
      margin: '0',
      padding: '0.6rem 1.5rem 0.8rem',
      maxHeight: '85vh',
      overflowY: 'auto',
      background: 'rgb(var(--color-background, 255 255 255))',
      borderTop: '0.1rem solid rgba(var(--color-foreground, 0 0 0), 0.12)',
      boxShadow: '0 -0.2rem 0.8rem rgba(0,0,0,0.12)'
    };

    function setStuck(next) {
      if (next === stuck) { return; }
      if (next) {
        for (var k in STUCK_STYLE) { box.style[k] = STUCK_STYLE[k]; }
        box.classList.add('is-stuck');
        // The box leaves the flow and becomes a compact fixed bar. DON'T hold a
        // top spacer the size of the (huge) full picker — that left an ugly blank
        // hole where the box used to be. Let the content flow up naturally.
        spacer.style.display = 'none';
      } else {
        for (var k2 in STUCK_STYLE) { box.style[k2] = ''; }
        box.classList.remove('is-stuck');
        spacer.style.display = 'none';
      }
      stuck = next;
      // Width changed (compact bar vs in-flow column) — re-measure swatch rows.
      // On close, also drop any "Fewer Colors" expansion so the bar comes back
      // with the colors collapsed to one row.
      if (!next && typeof window.__pbxSeeMoreCollapse === 'function') {
        window.__pbxSeeMoreCollapse();
      } else if (typeof window.__pbxSeeMoreRefresh === 'function') {
        window.__pbxSeeMoreRefresh();
      }
    }

    // The Add to Cart's natural top/bottom relative to the box top. Cached, and
    // only recomputed while NOT stuck (full in-flow layout) so the compact stuck
    // layout can't corrupt the values.
    var atcTop = 0, atcBot = 0;
    function measureAtc() {
      if (stuck) { return; }
      var atc = box.querySelector('.product-form__submit') || box.querySelector('[name="add"]');
      var boxTop = box.getBoundingClientRect().top;
      if (atc) {
        var r = atc.getBoundingClientRect();
        atcTop = r.top - boxTop;
        atcBot = r.bottom - boxTop;
      } else {
        atcTop = 0;
        atcBot = box.offsetHeight;
      }
    }

    // The sentinel marks the box's natural top (stable, never moves on
    // stick/unstick); the spacer keeps the page from jumping; the 24px hysteresis
    // stops jitter at the edges.
    //
    // Unsticking is additionally debounced by UNSTICK_DELAY: while the user is
    // actively scrolling, the ATC can graze the viewport edge for a frame or two —
    // hiding the bar the instant that happens looks jumpy. The bar only closes
    // once the ATC has stayed in view for the whole delay.
    var UNSTICK_DELAY = 600; // ms
    var unstickSince = 0;
    function check() {
      if (!mq.matches || getVariant() !== 'version') { setStuck(false); return; }
      var vh = window.innerHeight;
      // While the user has tapped "Change" to jump up to the full picker, keep the
      // bar unstuck so the in-flow options are usable. Resume normal sticky
      // behaviour only once the whole buy box has scrolled out of view again.
      if (suspend) {
        var br = box.getBoundingClientRect();
        if (br.bottom < -40 || br.top > vh + 40) { suspend = false; }
        else { setStuck(false); return; }
      }
      // Key the trigger off the SENTINEL (which sits above the box and never moves
      // on stick/unstick) + the real Add to Cart's cached position. This is stable:
      // unlike the description, it doesn't shift when the box leaves the flow, so
      // toggling sticky can't feed back and cause flicker.
      measureAtc();
      var sTop = sentinel.getBoundingClientRect().top;
      var natTop = sTop + atcTop;        // ATC top in the viewport (natural)
      var natBot = sTop + atcBot;        // ATC bottom in the viewport (natural)
      if (!stuck) {
        if (natBot <= 0 || natTop >= vh) {                        // ATC out of view
          unstickSince = 0;
          setStuck(true);
        }
      } else {
        if (natBot > 24 && natTop < vh - 24) {                    // ATC back in view
          var now = (window.performance && performance.now) ? performance.now() : Date.now();
          if (!unstickSince) { unstickSince = now; }
          else if (now - unstickSince >= UNSTICK_DELAY) {
            unstickSince = 0;
            setStuck(false);
          }
        } else {
          unstickSince = 0; // ATC left view again before the delay ran out
        }
      }
    }
    // Exposed so StickyBuyBoxAB.setVariant() can re-apply live without a reload.
    window.__stickyBuyBoxCheck = check;

    // --- Color change inside the stuck bar -----------------------------------
    // The bar deliberately stays OPEN and stuck on every change: the whole point
    // is choosing a color, then a size, then adding to cart without scrolling.
    // (It used to unstick and scroll away on every change, which forced a scroll
    // between each step.)
    //
    // But a color change swaps the gallery to the new color, and from the bottom
    // of the page that swap happens off-screen. So on a COLOR change, scroll the
    // user back up to the gallery so they always see the new color's images. The
    // bar stays fixed at the bottom during the ride; if the in-flow Add to Cart
    // ends up back in view, the normal (delayed) unstick takes over.

    // Is the option group that fired this change a color picker? Read from the
    // control itself, since the picker replaces its own markup right after.
    function isColorControl(el) {
      var name = '';
      if (el.matches('select')) {
        var m = (el.getAttribute('name') || '').match(/options\[(.+)\]/);
        name = m ? m[1] : '';
      } else {
        name = (el.getAttribute('name') || '').replace(/-\d+$/, '');
      }
      if (/colou?r/i.test(name)) { return true; }
      // Fall back to the group's own label, for markup that names radios by index.
      var group = el.closest('.product-form__input');
      var legend = group && group.querySelector('legend, .form__label');
      return !!(legend && /colou?r/i.test(legend.textContent || ''));
    }

    box.addEventListener('change', function (e) {
      if (!stuck) { return; }
      var t = e.target;
      if (!t || !t.matches('input[type="radio"], select')) { return; }
      if (!isColorControl(t)) { return; }

      // A long, expanded swatch list covers the gallery it was just used to
      // change. Collapse it back to one row so the new color's images are
      // visible in full. (The picker re-renders on change and the swatch
      // observer re-applies the clip on the next frame; clearing the expansion
      // state here is what makes that re-apply land collapsed.)
      if (typeof window.__pbxSeeMoreCollapse === 'function') {
        window.__pbxSeeMoreCollapse();
      }

      // Measure on the next frame, after the collapse + the picker's re-render
      // have settled, so the gallery is read at its real position.
      requestAnimationFrame(function () {
        var media = document.querySelector('.product__media-wrapper');
        // Land with the top of the gallery just under the header, so the image
        // swap is what the user sees. No media element -> just go to the top.
        var top = media
          ? window.pageYOffset + media.getBoundingClientRect().top - 70
          : 0;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    });

    // --- Condensed summary + "Change" jump -----------------------------------
    // In the stuck bar the full option grid is hidden (CSS) and replaced by a
    // compact "Color: Black [chip] Change / Size: XS Change" summary so the bar
    // can't grow to fill the viewport when a product has many options. Tapping
    // "Change" scrolls up to the real in-flow picker and suspends the sticky.
    function gotoPicker(targetGroup) {
      suspend = true;
      setStuck(false);
      // Un-sticking returns the buy box to normal flow; measure on the next frame
      // so the target (the option group just tapped) sits at its real, in-flow
      // position. Scroll it just below the header. If the whole buy box fits, keep
      // the Add-to-Cart in view too by not over-scrolling past it.
      requestAnimationFrame(function () {
        var headerOffset = 70;
        var target = (targetGroup && box.contains(targetGroup)) ? targetGroup : box;
        var top = window.pageYOffset + target.getBoundingClientRect().top - headerOffset;

        // Don't scroll so far that the Add to Cart drops below the fold: cap the
        // scroll at the position that pins the ATC bottom to the viewport bottom.
        var atc = box.querySelector('.product-form__submit') || box.querySelector('[name="add"]');
        if (atc) {
          var atcBottomDoc = window.pageYOffset + atc.getBoundingClientRect().bottom;
          var maxTop = atcBottomDoc - window.innerHeight + 20;
          if (top > maxTop) { top = maxTop; }
        }
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }

    var summaryEl = null;
    function buildSummary() {
      var variantPart = box.querySelector('.pbx-variant');
      if (!variantPart) { return; }
      if (!summaryEl) {
        summaryEl = document.createElement('div');
        summaryEl.className = 'pbx-summary';
        variantPart.insertBefore(summaryEl, variantPart.firstChild);
      }
      var groups = variantPart.querySelectorAll('variant-selects .product-form__input');
      var rows = '';
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i];
        var name = '', value = '', chip = '';
        var radio = g.querySelector('input[type="radio"]:checked');
        var select = g.querySelector('select');
        if (radio) {
          name = (radio.getAttribute('name') || '').replace(/-\d+$/, '');
          value = radio.value || '';
          var lbl = radio.closest('label');
          var span = lbl && lbl.querySelector('span[style*="background"]');
          if (span) {
            var bg = span.style.backgroundImage || span.style.backgroundColor || '';
            if (bg && bg !== 'none') { chip = bg; }
          }
        } else if (select) {
          var m = (select.getAttribute('name') || '').match(/options\[(.+)\]/);
          name = m ? m[1] : '';
          value = select.value || '';
        }
        if (!name || !value) { continue; }
        var isColor = /colou?r/i.test(name) || !!chip;
        var safeName = name.replace(/[<>&]/g, '');
        var safeVal = value.replace(/[<>&]/g, '');
        var valHtml = isColor
          ? '<span class="pbx-sum-text">' + safeVal + '</span>'
            + (chip ? '<span class="pbx-sum-chip" style="background:' + chip.replace(/"/g, '') + '"></span>' : '')
          : '<span class="pbx-sum-box">' + safeVal + '</span>';
        rows += '<span class="pbx-sum-row" data-group-index="' + i + '">'
              + '<span class="pbx-sum-label">' + safeName + ':</span>'
              + valHtml
              + '<button type="button" class="pbx-sum-change">Change</button>'
              + '</span>';
      }
      summaryEl.innerHTML = rows;
      var btns = summaryEl.querySelectorAll('.pbx-sum-change');
      for (var b = 0; b < btns.length; b++) {
        (function (button) {
          button.addEventListener('click', function () {
            var idx = parseInt(button.parentNode.getAttribute('data-group-index'), 10);
            gotoPicker(groups[idx]);
          });
        })(btns[b]);
      }
    }

    // Rebuild when a variant option changes (radios/select fire 'change', which
    // bubbles to the box) so the summary always reflects the current selection.
    box.addEventListener('change', buildSummary);
    buildSummary();

    // Drive the check from a rAF loop instead of the scroll event: it re-evaluates
    // every frame regardless of HOW the page scrolls (window, inner container,
    // momentum scrolling, etc.), so the trigger can't be missed. check() only reads
    // geometry + toggles on change, so it's cheap. The hysteresis keeps it
    // flicker-free. The loop only spins while the A/B variant is on, so a visitor
    // in 'default' pays nothing for it.
    function loop() {
      if (!running) { return; }
      check();
      requestAnimationFrame(loop);
    }

    window.__stickyBuyBoxRun = function () {
      if (running) { return; }
      running = true;
      requestAnimationFrame(loop);
      check();
    };
    // Turning the A/B flag off: unstick, drop any swatch collapse, stop the loop.
    window.__stickyBuyBoxStop = function () {
      running = false;
      setStuck(false);
    };

    window.addEventListener('resize', check, { passive: true });
    if (mq.addEventListener) { mq.addEventListener('change', check); }
    else if (mq.addListener) { mq.addListener(check); }

    window.__stickyBuyBoxRun();
  }

  /* ===================== boot ============================================== */
  var booted = false;
  function start() {
    if (getVariant() !== 'version') { return; }
    if (booted) {
      // Already initialised and then switched off — just restart the loop.
      if (typeof window.__stickyBuyBoxRun === 'function') { window.__stickyBuyBoxRun(); }
      return;
    }
    if (!document.querySelector('.product-buy-box, .product-form__buttons')) { return; }
    booted = true;
    initSwatchCollapse();
    initStickyBox();
  }

  // Hold until the page has finished loading so the buy box (and any late app
  // block inside it) is fully rendered before we measure anything. Re-tried once
  // after a beat in case something renders even later.
  if (document.readyState === 'complete') { start(); }
  else { window.addEventListener('load', start); }
  setTimeout(function () { try { start(); } catch (e) {} }, 1500);
})();




